import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {I18nService} from '../services/I18nService';
import {WorklogStore} from '../services/WorklogStore';
import {StorageService} from '../services/StorageService';
import {Timer} from '../services/Timer';
import {JiraClient} from '../services/JiraClient';
import {SyncQueue} from '../services/SyncQueue';
import {JiraIssue} from "../types/components";
import {AppSettings, Favorite, WorklogJSON} from "../types/storage";

interface AppContextType {
  // Services
  i18n: I18nService;
  settings: AppSettings;
  store: WorklogStore;
  timer: Timer;
  jiraClient: JiraClient;
  syncQueue: SyncQueue;

  // Language
  language: string;
  setLanguage: (lang: string, persistToServer?: boolean) => Promise<void>;

  // Auto sync
  autoSync: boolean;
  setAutoSync: (enabled: boolean, persistToServer?: boolean) => Promise<void>;
  // Theme
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark', persistToServer?: boolean) => Promise<void>;

  // Favorites
  favorites: Favorite[];
  addFavorite: (fav: Favorite) => Promise<void>;
  updateFavorite: (fav: Favorite) => Promise<void>;
  removeFavorite: (id: string) => Promise<void>;
  reorderFavorites: (fromIndex: number, toIndex: number) => Promise<void>;

  // Methods
  signIn: (email: string, token: string) => Promise<any>;
  signOut: () => Promise<void>;
  exportAll: () => Promise<string>;
  importAll: (data: string) => Promise<void>;
  searchIssues: (query: string, exact?: boolean) => Promise<JiraIssue[]>;

  // Timer methods
  startTimer: (issueKey: string, description: string, start?: Date) => void;
  stopTimer: () => any;

  // Auth state
  isConnected: boolean;
  isCheckingConnection: boolean;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({children}) => {
  // Initialize services (from app.module.js constructor)
  const [authToken, setAuthToken] = useState<string>(() => StorageService.loadAuthToken());
  const [settings, setSettingsState] = useState<AppSettings>(() => StorageService.loadSettings());
  const [i18n] = useState(() => {
    const svc = new I18nService();
    // Ensure correct language is set before first render
    svc.setLanguage((typeof window !== 'undefined' && settings && settings.lang) ? settings.lang : 'en');
    return svc;
  });
  const [store] = useState(() => new WorklogStore());
  const [timer] = useState(() => new Timer());
  const [jiraClient] = useState(() => new JiraClient());
  // A dummy version state used to force re-render when SyncQueue updates the store
  const [_syncVersion, setSyncVersion] = useState(0);
  const [syncQueue] = useState(() => new SyncQueue(() => {
    // Trigger a Provider re-render so consumers (e.g., TimerView) recompute totals
    setSyncVersion(v => v + 1);
  }));

  // Connection state
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(false);

  // Favorites state
  const [favorites, setFavorites] = useState<Favorite[]>(() => StorageService.loadFavorites());

  const _persistFavorites = async (next: Favorite[], persistToServer: boolean = true) => {
    setFavorites(next);
    StorageService.saveFavorites(next);

    if (persistToServer && authToken) {
      await jiraClient.updateUserFavorites(next);
    }
  };

  const addFavorite = async (fav: Favorite) => {
    await _persistFavorites([...favorites, fav]);
  };

  const updateFavorite = async (fav: Favorite) => {
    const next = favorites.map(f => (f.id === fav.id ? fav : f));
    await _persistFavorites(next);
  };

  const removeFavorite = async (id: string) => {
    await _persistFavorites(favorites.filter(f => f.id !== id));
  };

  const reorderFavorites = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= favorites.length || toIndex > favorites.length - 1) {
      return;
    }
    const next = favorites.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    await _persistFavorites(next);
  };

  const _persistSettings = async (updatedSettings: AppSettings, persistToServer: boolean = true) => {
    setSettingsState(updatedSettings);
    StorageService.saveSettings(updatedSettings);

    if (persistToServer && authToken) {
      await jiraClient.updateUserSettings(updatedSettings);
    }
  };

  const _persistWorklogs = (next: WorklogJSON[]) => {
    StorageService.saveWorklogs(next);
    if (store && typeof store.reload === 'function') {
      store.reload();
    }
  }

  const setLanguage = async (lang: string, persistToServer: boolean = true) => {
    i18n.setLanguage(lang);

    const updatedSettings = {...settings, lang};
    await _persistSettings(updatedSettings, persistToServer);

    // Notify electron app of language change if running in electron
    if (Boolean(window?.electronAPI)) {
      window.electronAPI.setLanguage(lang);
    }
  };

  const setAutoSync = async (enabled: boolean, persistToServer: boolean = true) => {
    const updatedSettings = {...settings, autoSync: true}; // force autoSync to always true
    await _persistSettings(updatedSettings, persistToServer);
  };

  const setTheme = async (next: 'light' | 'dark', persistToServer: boolean = true) => {
    const updatedSettings = {...settings, theme: next};
    await _persistSettings(updatedSettings, persistToServer);
  };

  const signIn = async (email: string, token: string) => {
    setIsCheckingConnection(true);
    try {
      const newAuthToken = await jiraClient.login(email, token);
      if (newAuthToken) {
        setAuthToken(newAuthToken);

        const userSettings = await jiraClient.getUserSettings();
        if (userSettings) {
          await _persistSettings({...settings, ...userSettings} as AppSettings, false);
        }
        const userFavorites = await jiraClient.getUserFavorites();
        if (userFavorites) {
          await _persistFavorites(userFavorites, false);
        }
        setIsConnected(true);

        return {
          message: `Logged in successfully`,
        };
      } else {
        throw new Error('Login failed');
      }
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const signOut = async () => {
    // Clear all persisted local data
    StorageService.reset();
    // Reset in-memory state to safe defaults
    const defaults = StorageService.loadSettings(); // will return defaults after clear
    setSettingsState(defaults);
    setFavorites([]);
    if (store && typeof store.reload === 'function') {
      store.reload();
    }
    setIsConnected(false);
  };

  const searchIssues = async (query: string, exact?: boolean) => {
    return await jiraClient.searchIssues(query, exact);
  }

  const exportAll = async () => {
    return StorageService.exportAll();
  };

  const importAll = async (data: string) => {
    const res = StorageService.importAll(data);
    if (res.settings) {
      await _persistSettings(res.settings, true);
    }
    if (res.favorites) {
      await _persistFavorites(res.favorites, true);
    }
    if (res.worklogs) {
      _persistWorklogs(res.worklogs);
    }
  };

  const startTimer = (issueKey: string, description: string, start?: Date) => {
    const when = start ?? new Date();
    timer.start(issueKey, description, when);
  };

  const stopTimer = () => {
    const entry = timer.stop();
    if (entry) {
      // Create worklog from timer entry (similar to app.module.js logic)
      return {
        issueKey: entry.issueKey,
        description: entry.description,
        start: entry.start,
        end: entry.end,
        status: settings.autoSync ? 'queued' : 'local'
      };
    }
    return null;
  };

  // Apply theme to document root
  useEffect(() => {
    try {
      const root = document.documentElement;
      if (root && settings.theme) {
        root.setAttribute('data-theme', settings.theme);
      }
    } catch {
    }
  }, [settings.theme]);

  // On mount (and when credentials change), try to validate connection silently
  useEffect(() => {
    const isLoggedIn = Boolean(authToken && authToken.length > 0);
    if (!isLoggedIn) {
      setIsConnected(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsCheckingConnection(true);
      try {
        await jiraClient.getUser();
        if (!cancelled) setIsConnected(true);
      } catch {
        if (!cancelled) setIsConnected(false);
      } finally {
        if (!cancelled) setIsCheckingConnection(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const value: AppContextType = {
    i18n,
    settings,
    store,
    timer,
    jiraClient,
    syncQueue,
    language: settings.lang,
    setLanguage,
    // autoSync: settings.autoSync,
    autoSync: true, // force autoSync to always true
    setAutoSync,
    theme: settings.theme,
    setTheme,
    favorites,
    addFavorite,
    updateFavorite,
    removeFavorite,
    reorderFavorites,
    signIn,
    signOut,
    searchIssues,
    exportAll,
    importAll,
    startTimer,
    stopTimer,
    isConnected,
    isCheckingConnection,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppContextProvider');
  }
  return context;
};